// Unit 1: Exploring One-Variable Data (15-23% of AP Exam)

export const unit1Lessons = [
  {
    id: 'unit1-lesson1',
    title: 'Analyzing Categorical Data',
    unit: 'Unit 1: Exploring One-Variable Data',
    standards: ['AP Statistics Unit 1.1', 'AP Statistics Unit 1.2'],
    vocabulary: [
      {
        term: 'Categorical Variable',
        definition: 'A variable that takes on values that are names or labels (e.g., color, gender, yes/no).'
      },
      {
        term: 'Frequency Table',
        definition: 'A table showing the number of observations in each category.'
      },
      {
        term: 'Relative Frequency',
        definition: 'The proportion or percentage of observations in each category.'
      },
      {
        term: 'Bar Chart',
        definition: 'A graphical display of categorical data using bars with heights representing frequencies.'
      },
      {
        term: 'Pie Chart',
        definition: 'A circular graph divided into sectors representing the relative frequencies of categories.'
      }
    ],
    concept: 'Categorical data represents qualities or characteristics that can be classified into groups. We use frequency distributions, bar charts, and pie charts to display and analyze categorical variables.',
    workedExample: {
      problem: 'A survey of 200 students asked about their preferred social media platform. The results: Instagram (80), TikTok (65), Snapchat (35), Twitter (20). Create a relative frequency table and describe the distribution.',
      steps: [
        'Calculate relative frequencies by dividing each count by the total (200)',
        'Instagram: 80/200 = 0.40 or 40%',
        'TikTok: 65/200 = 0.325 or 32.5%',
        'Snapchat: 35/200 = 0.175 or 17.5%',
        'Twitter: 20/200 = 0.10 or 10%',
        'Interpretation: Instagram is the most popular platform (40%), followed by TikTok (32.5%). Snapchat and Twitter have notably lower usage among students.'
      ]
    },
    teacherTip: 'Always check that relative frequencies sum to 1.00 (or 100%). A common AP error is forgetting to include all categories or miscalculating proportions.',
    practiceQuestions: [
      {
        id: 'u1l1q1',
        question: 'Which of the following is a categorical variable?',
        options: [
          'Height in centimeters',
          'Eye color',
          'Temperature in degrees',
          'Weight in kilograms'
        ],
        correctAnswer: 1,
        explanation: 'Eye color is categorical because it represents a quality (blue, brown, green) rather than a numerical measurement.'
      },
      {
        id: 'u1l1q2',
        question: 'In a frequency table with 5 categories and 250 total observations, one category has a relative frequency of 0.28. How many observations are in that category?',
        options: ['28', '56', '70', '112'],
        correctAnswer: 2,
        explanation: '0.28 × 250 = 70 observations in that category.'
      },
      {
        id: 'u1l1q3',
        question: 'What is the primary advantage of using relative frequencies instead of frequencies?',
        options: [
          'Easier to calculate',
          'Allows comparison between datasets of different sizes',
          'Always produces whole numbers',
          'Requires less data'
        ],
        correctAnswer: 1,
        explanation: 'Relative frequencies (proportions) allow meaningful comparisons between datasets with different total counts.'
      },
      {
        id: 'u1l1q4',
        question: 'A bar chart differs from a histogram in that:',
        options: [
          'Bar charts use vertical bars only',
          'Bar charts display categorical data with gaps between bars',
          'Bar charts cannot show frequencies',
          'Bar charts are always colored'
        ],
        correctAnswer: 1,
        explanation: 'Bar charts display categorical data with gaps between bars; histograms display quantitative data with bars touching.'
      },
      {
        id: 'u1l1q5',
        question: 'In a pie chart, a category with a relative frequency of 0.25 would occupy what angle?',
        options: ['25°', '45°', '90°', '120°'],
        correctAnswer: 2,
        explanation: '0.25 × 360° = 90°. The category occupies one-quarter of the circle.'
      },
      {
        id: 'u1l1q6',
        question: 'A survey of 300 voters shows: Democrat (135), Republican (120), Independent (45). What is the relative frequency of Independent voters?',
        options: ['0.10', '0.15', '0.20', '0.25'],
        correctAnswer: 1,
        explanation: '45/300 = 0.15 or 15% of voters are Independent.'
      },
      {
        id: 'u1l1q7',
        question: 'Which display is most appropriate for showing the distribution of blood types in a population?',
        options: [
          'Scatterplot',
          'Box plot',
          'Bar chart',
          'Histogram'
        ],
        correctAnswer: 2,
        explanation: 'Blood type is categorical, so a bar chart is the appropriate graphical display.'
      },
      {
        id: 'u1l1q8',
        question: 'The mode of a categorical distribution is:',
        options: [
          'The average category',
          'The middle category',
          'The category with the highest frequency',
          'The difference between highest and lowest frequencies'
        ],
        correctAnswer: 2,
        explanation: 'The mode is the category that occurs most frequently in the dataset.'
      },
      {
        id: 'u1l1q9',
        question: 'A frequency table shows: Category A (40), B (60), C (50), D (50). Which statement is correct?',
        options: [
          'The distribution is uniform',
          'Categories C and D are bimodal',
          'Category B is the mode',
          'The distribution is skewed'
        ],
        correctAnswer: 2,
        explanation: 'Category B has the highest frequency (60), making it the mode.'
      },
      {
        id: 'u1l1q10',
        question: 'When constructing a bar chart, which axis typically shows the categories?',
        options: [
          'Always the x-axis',
          'Always the y-axis',
          'Either axis is acceptable',
          'Neither axis'
        ],
        correctAnswer: 2,
        explanation: 'Bar charts can be vertical (categories on x-axis) or horizontal (categories on y-axis).'
      },
      {
        id: 'u1l1q11',
        question: 'Which measure of center is most appropriate for categorical data?',
        options: ['Mean', 'Median', 'Mode', 'Standard deviation'],
        correctAnswer: 2,
        explanation: 'The mode (most frequent category) is the only measure of center that makes sense for categorical data.'
      },
      {
        id: 'u1l1q12',
        question: 'A two-way table is used to analyze:',
        options: [
          'One categorical variable',
          'Two categorical variables',
          'One quantitative variable',
          'Two quantitative variables'
        ],
        correctAnswer: 1,
        explanation: 'A two-way table (contingency table) displays the relationship between two categorical variables.'
      },
      {
        id: 'u1l1q13',
        question: 'The sum of all relative frequencies in a complete frequency distribution must equal:',
        options: ['0', '0.5', '1.0', 'The sample size'],
        correctAnswer: 2,
        explanation: 'All relative frequencies must sum to 1.0 (or 100%) since they represent the entire dataset.'
      },
      {
        id: 'u1l1q14',
        question: 'Which graph would best show the proportion of students in each grade level at a school?',
        options: ['Histogram', 'Scatterplot', 'Pie chart', 'Boxplot'],
        correctAnswer: 2,
        explanation: 'Grade level is categorical, and a pie chart effectively shows the proportion of each category.'
      },
      {
        id: 'u1l1q15',
        question: 'In a bar chart, what does the height of each bar represent?',
        options: [
          'The category name',
          'The frequency or relative frequency',
          'The order of categories',
          'The width of the category'
        ],
        correctAnswer: 1,
        explanation: 'Bar height represents the frequency (count) or relative frequency (proportion) of each category.'
      },
      {
        id: 'u1l1q16',
        question: 'A segmented bar chart is useful for:',
        options: [
          'Comparing distributions across groups',
          'Showing individual data points',
          'Calculating standard deviation',
          'Finding outliers'
        ],
        correctAnswer: 0,
        explanation: 'Segmented bar charts allow visual comparison of categorical distributions across different groups.'
      },
      {
        id: 'u1l1q17',
        question: 'Which is NOT a characteristic of categorical data?',
        options: [
          'Represents qualities',
          'Can be ordered or unordered',
          'Can be averaged meaningfully',
          'Can be displayed in a bar chart'
        ],
        correctAnswer: 2,
        explanation: 'Categorical data cannot be averaged meaningfully because categories are not numerical values.'
      },
      {
        id: 'u1l1q18',
        question: 'A marginal distribution in a two-way table shows:',
        options: [
          'The distribution of one variable alone',
          'The relationship between two variables',
          'Only the corner values',
          'The center of the data'
        ],
        correctAnswer: 0,
        explanation: 'Marginal distributions show the totals for each row or column, representing one variable independently.'
      },
      {
        id: 'u1l1q19',
        question: 'When comparing two categorical distributions, which is most useful?',
        options: [
          'Compare frequencies directly',
          'Compare relative frequencies',
          'Compare only the modes',
          'Compare the sum of values'
        ],
        correctAnswer: 1,
        explanation: 'Comparing relative frequencies accounts for different sample sizes and allows fair comparison.'
      },
      {
        id: 'u1l1q20',
        question: 'Which statement about pie charts is FALSE?',
        options: [
          'Best for showing parts of a whole',
          'Each slice angle is proportional to frequency',
          'Useful for comparing many categories',
          'Total must equal 100%'
        ],
        correctAnswer: 2,
        explanation: 'Pie charts become difficult to read with many categories; bar charts are better for many categories.'
      },
      {
        id: 'u1l1q21',
        question: 'Ordinal categorical variables differ from nominal because:',
        options: [
          'They have more categories',
          'They have a natural order',
          'They use numbers',
          'They cannot be graphed'
        ],
        correctAnswer: 1,
        explanation: 'Ordinal variables have a natural ordering (e.g., small, medium, large) while nominal do not (e.g., colors).'
      },
      {
        id: 'u1l1q22',
        question: 'A conditional distribution in a two-way table shows:',
        options: [
          'The overall distribution',
          'The distribution of one variable given a specific value of the other',
          'Only the row totals',
          'The mode of each category'
        ],
        correctAnswer: 1,
        explanation: 'Conditional distributions show how one variable is distributed when the other variable is fixed at a specific value.'
      },
      {
        id: 'u1l1q23',
        question: 'Which graph allows for the easiest comparison of categorical data across two groups?',
        options: [
          'Side-by-side bar chart',
          'Single pie chart',
          'Histogram',
          'Scatterplot'
        ],
        correctAnswer: 0,
        explanation: 'Side-by-side bar charts place bars for each group next to each other for easy visual comparison.'
      },
      {
        id: 'u1l1q24',
        question: 'In a frequency table, cumulative frequency represents:',
        options: [
          'The most common category',
          'The running total of frequencies up to that category',
          'The average frequency',
          'The difference between highest and lowest'
        ],
        correctAnswer: 1,
        explanation: 'Cumulative frequency is the sum of frequencies from the first category up to and including the current category.'
      },
      {
        id: 'u1l1q25',
        question: 'For ordinal categorical data, which additional measure makes sense?',
        options: [
          'Mean',
          'Standard deviation',
          'Median',
          'Correlation'
        ],
        correctAnswer: 2,
        explanation: 'Since ordinal data has order, the median (middle category) is a meaningful measure of center.'
      },
      {
        id: 'u1l1q26',
        question: 'A Pareto chart is a special bar chart that:',
        options: [
          'Uses colors',
          'Orders categories from highest to lowest frequency',
          'Shows only percentages',
          'Includes a line graph'
        ],
        correctAnswer: 1,
        explanation: 'Pareto charts arrange categories in descending order of frequency, often with a cumulative percentage line.'
      },
      {
        id: 'u1l1q27',
        question: 'Which is an example of a binary categorical variable?',
        options: [
          'Blood type (A, B, AB, O)',
          'Pass/Fail',
          'Grade level (9, 10, 11, 12)',
          'Eye color'
        ],
        correctAnswer: 1,
        explanation: 'Binary categorical variables have exactly two categories, such as pass/fail, yes/no, or male/female.'
      },
      {
        id: 'u1l1q28',
        question: 'When analyzing a two-way table, which percentage type shows the distribution within each row?',
        options: [
          'Total percentages',
          'Row percentages',
          'Column percentages',
          'Marginal percentages'
        ],
        correctAnswer: 1,
        explanation: 'Row percentages show conditional distributions within each row, summing to 100% across columns.'
      },
      {
        id: 'u1l1q29',
        question: 'A mosaic plot is used to:',
        options: [
          'Display one quantitative variable',
          'Show association between two categorical variables',
          'Calculate probabilities',
          'Find outliers'
        ],
        correctAnswer: 1,
        explanation: 'Mosaic plots visualize the relationship between two categorical variables using area to represent frequency.'
      },
      {
        id: 'u1l1q30',
        question: 'Which statement about bar charts is TRUE?',
        options: [
          'Bars must always touch',
          'The order of bars matters for nominal data',
          'Bar width has no meaning',
          'Bars must be the same color'
        ],
        correctAnswer: 2,
        explanation: 'In bar charts, bar width is arbitrary and has no meaning; only height represents the data.'
      }
    ]
  },
  {
    id: 'unit1-lesson2',
    title: 'Quantitative Data: Distributions and Shape',
    unit: 'Unit 1: Exploring One-Variable Data',
    standards: ['AP Statistics Unit 1.3', 'AP Statistics Unit 1.4'],
    vocabulary: [
      {
        term: 'Quantitative Variable',
        definition: 'A variable that takes on numerical values representing amounts or measurements.'
      },
      {
        term: 'Distribution',
        definition: 'The pattern of values a variable takes, including shape, center, spread, and unusual features.'
      },
      {
        term: 'Symmetric Distribution',
        definition: 'A distribution where the left and right sides are approximately mirror images.'
      },
      {
        term: 'Skewed Right',
        definition: 'A distribution with a long tail extending to the right (positive skew).'
      },
      {
        term: 'Skewed Left',
        definition: 'A distribution with a long tail extending to the left (negative skew).'
      },
      {
        term: 'Outlier',
        definition: 'An observation that is unusually far from the bulk of the data.'
      }
    ],
    concept: 'When describing quantitative data distributions, we always address SOCS: Shape (symmetric, skewed, bimodal), Outliers (unusual values), Center (mean or median), and Spread (range, IQR, standard deviation).',
    workedExample: {
      problem: 'Test scores: 65, 72, 75, 78, 80, 82, 85, 88, 90, 92, 95, 98. Describe the distribution using SOCS.',
      steps: [
        'Shape: Slightly skewed left (more scores clustered at higher end)',
        'Outliers: 65 appears unusually low compared to others',
        'Center: Median = (82+85)/2 = 83.5; Mean ≈ 83.3',
        'Spread: Range = 98-65 = 33; IQR will be smaller (more resistant)',
        'Complete description: "The distribution of test scores is slightly skewed left with one potential outlier at 65. The center is approximately 83-84, and scores vary from 65 to 98, a range of 33 points."'
      ]
    },
    teacherTip: 'On the AP exam, always describe distributions completely using SOCS. Don\'t just say "skewed" – specify direction and support with evidence from the data.',
    practiceQuestions: [
      {
        id: 'u1l2q1',
        question: 'A distribution with a long tail to the right is called:',
        options: [
          'Skewed left',
          'Skewed right',
          'Symmetric',
          'Uniform'
        ],
        correctAnswer: 1,
        explanation: 'A long right tail indicates right skew (positive skew), where most data is on the left with some extreme high values.'
      },
      {
        id: 'u1l2q2',
        question: 'Which of the following is NOT part of describing a distribution?',
        options: [
          'Shape',
          'Correlation',
          'Center',
          'Spread'
        ],
        correctAnswer: 1,
        explanation: 'SOCS (Shape, Outliers, Center, Spread) are used for distributions. Correlation applies to two-variable data.'
      },
      {
        id: 'u1l2q3',
        question: 'A bimodal distribution has:',
        options: [
          'One peak',
          'Two distinct peaks',
          'No peaks',
          'Three peaks'
        ],
        correctAnswer: 1,
        explanation: 'Bimodal means two modes or two distinct peaks in the distribution.'
      },
      {
        id: 'u1l2q4',
        question: 'In a symmetric distribution, which relationship typically holds?',
        options: [
          'Mean > Median',
          'Mean < Median',
          'Mean ≈ Median',
          'Cannot be determined'
        ],
        correctAnswer: 2,
        explanation: 'In symmetric distributions, the mean and median are approximately equal because there is no skew.'
      },
      {
        id: 'u1l2q5',
        question: 'A distribution is skewed right. Which is likely true?',
        options: [
          'Mean < Median',
          'Mean > Median',
          'Mean = Median',
          'Median = Mode'
        ],
        correctAnswer: 1,
        explanation: 'In right-skewed distributions, the mean is pulled toward the long right tail, making it larger than the median.'
      },
      {
        id: 'u1l2q6',
        question: 'Which graph is best for showing the shape of a quantitative distribution?',
        options: [
          'Bar chart',
          'Pie chart',
          'Histogram',
          'Two-way table'
        ],
        correctAnswer: 2,
        explanation: 'Histograms display the distribution of quantitative data, showing shape, center, and spread.'
      },
      {
        id: 'u1l2q7',
        question: 'Gaps in a distribution might indicate:',
        options: [
          'The data is symmetric',
          'Distinct subgroups or measurement error',
          'High variability',
          'Normal distribution'
        ],
        correctAnswer: 1,
        explanation: 'Gaps can suggest distinct subgroups in the data or potential issues with data collection.'
      },
      {
        id: 'u1l2q8',
        question: 'A uniform distribution has:',
        options: [
          'One distinct peak',
          'Two peaks',
          'Approximately equal frequencies across all values',
          'A long right tail'
        ],
        correctAnswer: 2,
        explanation: 'Uniform distributions have roughly equal frequencies for all values, appearing flat.'
      },
      {
        id: 'u1l2q9',
        question: 'When describing center, which measure is most resistant to outliers?',
        options: [
          'Mean',
          'Median',
          'Mode',
          'Range'
        ],
        correctAnswer: 1,
        explanation: 'The median is resistant because it depends only on the middle value(s), not extreme values.'
      },
      {
        id: 'u1l2q10',
        question: 'A dotplot is most useful for:',
        options: [
          'Large datasets (n > 100)',
          'Small to moderate datasets where individual values matter',
          'Categorical data',
          'Time series data'
        ],
        correctAnswer: 1,
        explanation: 'Dotplots show individual data points and are best for smaller datasets where you can see each observation.'
      },
      {
        id: 'u1l2q11',
        question: 'In a left-skewed distribution, which is typically largest?',
        options: [
          'Mean',
          'Median',
          'Mode',
          'All are equal'
        ],
        correctAnswer: 2,
        explanation: 'In left-skewed distributions: Mode > Median > Mean, with the mean pulled toward the long left tail.'
      },
      {
        id: 'u1l2q12',
        question: 'Which describes a bell-shaped distribution?',
        options: [
          'Uniform',
          'Bimodal',
          'Approximately normal',
          'Heavily skewed'
        ],
        correctAnswer: 2,
        explanation: 'Bell-shaped distributions are approximately normal: symmetric, unimodal, with specific spread characteristics.'
      },
      {
        id: 'u1l2q13',
        question: 'Stem-and-leaf plots are advantageous because:',
        options: [
          'They show individual data values',
          'They work well for large datasets',
          'They display categorical data',
          'They calculate statistics automatically'
        ],
        correctAnswer: 0,
        explanation: 'Stem-and-leaf plots retain individual data values while showing the distribution shape.'
      },
      {
        id: 'u1l2q14',
        question: 'A distribution with no clear peak is called:',
        options: [
          'Unimodal',
          'Bimodal',
          'Multimodal',
          'Uniform or flat'
        ],
        correctAnswer: 3,
        explanation: 'When frequencies are roughly equal across values, the distribution appears uniform or flat with no distinct peak.'
      },
      {
        id: 'u1l2q15',
        question: 'When comparing two distributions, you should compare:',
        options: [
          'Only their means',
          'Shape, center, and spread for both',
          'Only their shapes',
          'Only the number of outliers'
        ],
        correctAnswer: 1,
        explanation: 'Complete comparison requires examining shape, center, and spread for both distributions, in context.'
      },
      {
        id: 'u1l2q16',
        question: 'A distribution of incomes is typically:',
        options: [
          'Symmetric',
          'Skewed left',
          'Skewed right',
          'Uniform'
        ],
        correctAnswer: 2,
        explanation: 'Income distributions are typically right-skewed, with most people earning moderate amounts and few earning very high incomes.'
      },
      {
        id: 'u1l2q17',
        question: 'Clusters in a distribution suggest:',
        options: [
          'Uniform distribution',
          'Potential subgroups in the data',
          'Normal distribution',
          'Measurement error only'
        ],
        correctAnswer: 1,
        explanation: 'Clusters indicate possible subgroups or categories within the data that might warrant separate analysis.'
      },
      {
        id: 'u1l2q18',
        question: 'The "typical" value in a distribution is described by:',
        options: [
          'Spread',
          'Center',
          'Shape',
          'Outliers'
        ],
        correctAnswer: 1,
        explanation: 'Center (mean, median, or mode) describes the typical or central value of a distribution.'
      },
      {
        id: 'u1l2q19',
        question: 'When describing shape, you should mention:',
        options: [
          'Symmetry or skewness',
          'Number of peaks',
          'Presence of gaps or clusters',
          'All of the above'
        ],
        correctAnswer: 3,
        explanation: 'Complete shape description includes symmetry/skewness, modality (peaks), and unusual features like gaps.'
      },
      {
        id: 'u1l2q20',
        question: 'A back-to-back stemplot is used to:',
        options: [
          'Display one variable over time',
          'Compare distributions of two groups',
          'Show correlation',
          'Calculate percentiles'
        ],
        correctAnswer: 1,
        explanation: 'Back-to-back stemplots allow visual comparison of two distributions side-by-side.'
      },
      {
        id: 'u1l2q21',
        question: 'Which is NOT an unusual feature to mention when describing a distribution?',
        options: [
          'Outliers',
          'Gaps',
          'The sample size',
          'Clusters'
        ],
        correctAnswer: 2,
        explanation: 'Sample size is important but not an "unusual feature" of the distribution shape itself.'
      },
      {
        id: 'u1l2q22',
        question: 'Homework completion rates (0-100%) would likely be:',
        options: [
          'Symmetric',
          'Skewed right',
          'Skewed left',
          'Uniform'
        ],
        correctAnswer: 2,
        explanation: 'Homework completion is often left-skewed with most students completing most work, and a tail toward lower rates.'
      },
      {
        id: 'u1l2q23',
        question: 'A cumulative frequency graph shows:',
        options: [
          'Individual data values',
          'The running total of frequencies',
          'Only the mode',
          'Correlation'
        ],
        correctAnswer: 1,
        explanation: 'Cumulative frequency graphs plot the total number (or proportion) of observations up to each value.'
      },
      {
        id: 'u1l2q24',
        question: 'When the tail of a distribution points left, the distribution is:',
        options: [
          'Skewed right',
          'Skewed left',
          'Symmetric',
          'Bimodal'
        ],
        correctAnswer: 1,
        explanation: 'The tail indicates the direction of skew: left tail = left skew (negative skew).'
      },
      {
        id: 'u1l2q25',
        question: 'Describing variability means discussing:',
        options: [
          'How data values differ from each other',
          'The center of the data',
          'The sample size',
          'The variable name'
        ],
        correctAnswer: 0,
        explanation: 'Variability (spread) describes how much the data values differ from each other and from the center.'
      },
      {
        id: 'u1l2q26',
        question: 'A distribution with most values near the center and fewer at the extremes is:',
        options: [
          'Uniform',
          'Bell-shaped',
          'Heavily skewed',
          'Bimodal'
        ],
        correctAnswer: 1,
        explanation: 'This describes a bell-shaped (approximately normal) distribution.'
      },
      {
        id: 'u1l2q27',
        question: 'When asked to "describe the distribution," you must address:',
        options: [
          'Only the mean',
          'SOCS: Shape, Outliers, Center, Spread',
          'Only whether it is normal',
          'Just the five-number summary'
        ],
        correctAnswer: 1,
        explanation: 'Complete distribution descriptions always include SOCS, stated in context of the data.'
      },
      {
        id: 'u1l2q28',
        question: 'Age at retirement would likely show:',
        options: [
          'Right skew',
          'Left skew',
          'Perfect symmetry',
          'Bimodal distribution'
        ],
        correctAnswer: 1,
        explanation: 'Retirement age is often left-skewed with most people retiring around a typical age and fewer retiring very early.'
      },
      {
        id: 'u1l2q29',
        question: 'Mound-shaped distributions are characterized by:',
        options: [
          'Flat appearance',
          'Single peak in the middle',
          'Two distinct peaks',
          'Long tail on one side'
        ],
        correctAnswer: 1,
        explanation: 'Mound-shaped distributions have a single peak in the center and taper off on both sides.'
      },
      {
        id: 'u1l2q30',
        question: 'Which is the best first step when exploring a new quantitative dataset?',
        options: [
          'Calculate the mean',
          'Create a graphical display',
          'Test for normality',
          'Find outliers'
        ],
        correctAnswer: 1,
        explanation: 'Always start with a graphical display (histogram, dotplot, etc.) to see the overall pattern before calculating statistics.'
      }
    ]
  }
];